import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Diagnostics } from "../../../../../../services";
import Panel, {
	EmptyState,
	PanelHint,
	PanelTitle,
} from "../../components/Panel";
import DataTable, { Accent, TableScroll } from "../../components/DataTable";
import MemoryChart from "../../components/MemoryChart";
import LatencyChart from "../../components/LatencyChart";
import { useSystemPerformance } from "../../context";
import useRequest from "../../hooks/useRequest";
import {
	formatBytes,
	formatDelta,
	formatDuration,
	formatNumber,
	formatTime,
} from "../../utils/format";

const Root = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;

	min-width: 0;
	max-width: 100%;
`;

const Columns = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 16px;
	min-width: 0;
	max-width: 100%;

	@media (max-width: 1200px) {
		grid-template-columns: minmax(0, 1fr);
	}
`;

const ChartWrap = styled.div`
	width: 100%;
	min-width: 0;
	max-width: 100%;
`;

const Overview: React.FC = () => {
	const { t } = useTranslation();
	const { service, rangeMinutes, refreshToken } = useSystemPerformance();

	const overview = useRequest<Diagnostics.OverviewResponse>(
		() => Diagnostics.getOverview({ service, rangeMinutes }),
		[service, rangeMinutes, refreshToken],
	);

	const timeline = useRequest<Diagnostics.TimelineResponse>(
		() =>
			Diagnostics.getTimeline({
				service,
				from: Date.now() - rangeMinutes * 60_000,
				to: Date.now(),
			}),
		[service, rangeMinutes, refreshToken],
	);

	/**
	 * The allocation profile is collected per pod; the overview shows the slice
	 * of the busiest one.
	 */
	const allocations = useMemo(() => {
		const windows = (overview.data?.pods ?? [])
			.map((pod) => pod.allocations)
			.filter(Boolean) as Diagnostics.AllocationWindow[];

		return windows.sort((a, b) => b.totalSize - a.totalSize)[0] ?? null;
	}, [overview.data]);

	return (
		<Root>
			<Panel>
				<PanelTitle>
					{t("settings.systemPerformance.overview.pods")}
				</PanelTitle>

				{overview.data?.pods?.length ? (
					<TableScroll>
						<DataTable>
							<thead>
								<tr>
									<th>
										{t(
											"settings.systemPerformance.overview.pod",
										)}
									</th>
									<th>
										{t(
											"settings.systemPerformance.overview.status",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.overview.heapUsed",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.overview.heapDelta",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.overview.rss",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.overview.eventLoop",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.overview.clients",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.overview.subscriptions",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.overview.updated",
										)}
									</th>
								</tr>
							</thead>
							<tbody>
								{overview.data.pods.map((pod) => (
									<tr key={pod.pod}>
										<td className="wide">{pod.pod}</td>
										<td>
											<Accent
												tone={
													pod.online
														? undefined
														: "danger"
												}
											>
												{t(
													pod.online
														? "settings.systemPerformance.overview.online"
														: "settings.systemPerformance.overview.offline",
												)}
											</Accent>
										</td>
										<td className="numeric">
											{formatBytes(pod.latest?.heapUsed)}
										</td>
										<td className="numeric">
											<Accent
												tone={
													pod.heapUsedDelta > 0
														? "warning"
														: "muted"
												}
											>
												{formatDelta(pod.heapUsedDelta)}
											</Accent>
										</td>
										<td className="numeric">
											{formatBytes(pod.latest?.rss)}
										</td>
										<td className="numeric">
											{formatDuration(
												pod.latest?.eventLoopDelayMax,
											)}
										</td>
										<td className="numeric">
											{formatNumber(
												pod.latest?.extra?.wsClients,
											)}
										</td>
										<td className="numeric">
											{formatNumber(
												pod.latest?.extra
													?.subscriptions,
											)}
										</td>
										<td className="numeric">
											{formatTime(pod.latest?.ts)}
										</td>
									</tr>
								))}
							</tbody>
						</DataTable>
					</TableScroll>
				) : (
					<EmptyState>
						{overview.loading
							? "…"
							: t("settings.systemPerformance.overview.empty")}
					</EmptyState>
				)}
			</Panel>

			<Panel>
				<PanelTitle>
					{t("settings.systemPerformance.overview.memoryChart")}
				</PanelTitle>
				<ChartWrap>
					<MemoryChart
						pods={timeline.data?.pods ?? []}
						calls={timeline.data?.calls ?? []}
					/>
				</ChartWrap>
			</Panel>

			<Panel>
				<PanelTitle>
					{t("settings.systemPerformance.overview.latencyChart")}
				</PanelTitle>
				<PanelHint>
					{t("settings.systemPerformance.overview.latencyChartHint")}
				</PanelHint>
				<ChartWrap>
					<LatencyChart pods={timeline.data?.pods ?? []} />
				</ChartWrap>
			</Panel>

			<Columns>
				<Panel>
					<PanelTitle>
						{t("settings.systemPerformance.overview.allocations")}
					</PanelTitle>
					<PanelHint>
						{t(
							"settings.systemPerformance.overview.allocationsHint",
						)}
					</PanelHint>

					{allocations?.byModule?.length ? (
						<TableScroll>
							<DataTable>
								<thead>
									<tr>
										<th>
											{t(
												"settings.systemPerformance.overview.module",
											)}
										</th>
										<th className="numeric">
											{t(
												"settings.systemPerformance.overview.size",
											)}
										</th>
									</tr>
								</thead>
								<tbody>
									{allocations.byModule.map((entry) => (
										<tr key={entry.name}>
											<td className="wide">
												{entry.name}
											</td>
											<td className="numeric">
												{formatBytes(entry.selfSize)}
											</td>
										</tr>
									))}
								</tbody>
							</DataTable>
						</TableScroll>
					) : (
						<EmptyState>
							{t("settings.systemPerformance.overview.empty")}
						</EmptyState>
					)}
				</Panel>

				<Panel>
					<PanelTitle>
						{t("settings.systemPerformance.overview.function")}
					</PanelTitle>

					{allocations?.byFunction?.length ? (
						<TableScroll>
							<DataTable>
								<thead>
									<tr>
										<th>
											{t(
												"settings.systemPerformance.overview.function",
											)}
										</th>
										<th className="numeric">
											{t(
												"settings.systemPerformance.overview.size",
											)}
										</th>
									</tr>
								</thead>
								<tbody>
									{allocations.byFunction.map((entry) => (
										<tr key={`${entry.file}#${entry.name}`}>
											<td className="wide">
												{entry.name}
												{entry.file ? (
													<Accent tone="muted">
														{` · ${entry.file}`}
													</Accent>
												) : null}
											</td>
											<td className="numeric">
												{formatBytes(entry.selfSize)}
											</td>
										</tr>
									))}
								</tbody>
							</DataTable>
						</TableScroll>
					) : (
						<EmptyState>
							{t("settings.systemPerformance.overview.empty")}
						</EmptyState>
					)}
				</Panel>
			</Columns>
		</Root>
	);
};

export default Overview;
